/**
 * InvConsistencyExplainer — explainer for the INV.CONSISTENCY Dual Pair Guarantee.
 *
 * INV.CONSISTENCY pairs INV.ESTABLISH (I1, F-algebra) with INV.PRESERVE (I2,
 * F-coalgebra) along the F-algebra ⊣ F-coalgebra adjunction. Deploying both
 * sides is strictly stronger than deploying either alone; the joint
 * guarantee is the structural envelope whose unit and counit witness the
 * coherence between "what was built" and "what can be observed."
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AdjunctionDiagram,
  CounterModelGrid,
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  FONT,
  GRAY,
  THEOREM_COLOR,
  withAlpha,
} from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const COUNTER_MODELS = [
  {
    title: "I1 alone — drift",
    body: "Establish without preserve: a builder produces a valid initial state, then arbitrary transitions wander into invalid territory. Establishment is real but transient.",
  },
  {
    title: "I2 alone — vacuity",
    body: "Preserve without establish: every transition preserves the invariant — but no inhabitant of Valid was ever constructed. The guarantee is vacuously true.",
  },
] as const;

const ACCENT = DUAL_PAIR_COLOR["INV.CONSISTENCY"].color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-inv-consistency",
  kind: "SSM · Dual Pair Guarantee (S1→S1)",
  title: "INV.CONSISTENCY",
  subtitle: "I1 + I2 · F-algebra ⊣ F-coalgebra",
  accent: ACCENT,
  accentAlt: COMPOSITIONALITY_PRINCIPLE_COLOR.ALWAYS.color,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "INV.ESTABLISH (I1)",
    href: "#explainer-inv-establish",
    accent: THEOREM_COLOR["INV.ESTABLISH"],
  },
  {
    label: "INV.PRESERVE (I2)",
    href: "#explainer-inv-preserve",
    accent: THEOREM_COLOR["INV.PRESERVE"],
  },
  {
    label: "ALWAYS (SDP dual)",
    href: "#explainer-always",
    accent: COMPOSITIONALITY_PRINCIPLE_COLOR.ALWAYS.color,
  },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "Bialgebra", href: "#explainer-bialgebra" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>INV.CONSISTENCY</strong> says: every valid value is built validly, and every step of a
      valid value stays valid — and these two facts cohere. One side alone is not enough.
    </p>
  ),
  intuition: (
    <>
      <p>
        Imagine a ledger. INV.ESTABLISH alone says "you can only <em>open</em> the ledger in a
        balanced state." INV.PRESERVE alone says "every step preserves whatever invariant you
        started with." The first does not stop the ledger from drifting; the second is vacuously
        true if you never opened a ledger.
      </p>
      <p>
        INV.CONSISTENCY is the joint guarantee — and crucially, it is{" "}
        <strong>strictly stronger</strong> than either side alone. The adjunction{" "}
        <MathInline>{tex`\text{F-algebra} \dashv \text{F-coalgebra}`}</MathInline> certifies that
        the canonical morphism <MathInline>{tex`\mu F \to \nu F`}</MathInline> exists and is
        structural, not stipulated.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        For an invariant functor <MathInline>{tex`F`}</MathInline>, INV.CONSISTENCY pairs:
      </p>
      <ul>
        <li>
          <strong>I1.</strong> Initial F-algebra <MathInline>{tex`(\mu F, \alpha)`}</MathInline>{" "}
          with <MathInline>{tex`\alpha : F(\mu F) \to \mu F`}</MathInline> an isomorphism.
        </li>
        <li>
          <strong>I2.</strong> Terminal F-coalgebra <MathInline>{tex`(\nu F, \gamma)`}</MathInline>{" "}
          with <MathInline>{tex`\gamma : \nu F \to F(\nu F)`}</MathInline> an isomorphism.
        </li>
      </ul>
      <p>
        The unique morphism <MathInline>{tex`\mathrm{canon} : \mu F \to \nu F`}</MathInline> is the
        comparison map. <strong>INV.CONSISTENCY</strong> requires that{" "}
        <MathInline>{tex`\mathrm{canon}`}</MathInline> is structural — every constructed value has a
        coherent observation history, and every observable behavior is realized by some
        construction.
      </p>
      <AdjunctionDiagram
        leftCat="F-Alg(F)"
        rightCat="F-Coalg(F)"
        leftAdjoint="Free_F"
        rightAdjoint="Cofree_F"
        accent={ACCENT}
      />
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> The two component theorems each have a
        counter-model that the other rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="INV.CONSISTENCY counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        Combining I1 and I2 along the adjunction closes both gaps in one envelope. PR #1544's Agda
        mechanization proves this constructively in <code>StrictnessCounterModel.agda</code>: each
        component admits a counter-model, the combination admits none.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing INV.CONSISTENCY requires bypassing both
        INV.ESTABLISH and INV.PRESERVE, or substituting a non-isomorphic{" "}
        <MathInline>{tex`\alpha`}</MathInline> or <MathInline>{tex`\gamma`}</MathInline> on one side
        — both moves leave the kernel category and so vacate the guarantee rather than violate it.
      </p>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Dual Pair Guarantees",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Joint Neutralization (Agda)",
      path: "agda/Kernel/INV/CONSISTENCY/Neutralization.agda",
      note: "Existing in-repo joint INV.CONSISTENCY proof.",
    },
    {
      label: "Joint Invariant Guarantee (Agda)",
      path: "agda/Kernel/INV/JointInvariantGuarantee.agda",
    },
    {
      label: "Structural Invariant Safety Guarantees manuscript (PR #1544)",
      path: "https://github.com/InvariantHoldings/categorical-safety-kernel/pull/1544",
      note: "Contrib manuscript with the DPG narrative, InvariantDualPair record, HylomorphismComposition, and StrictnessCounterModel. Land via PR #1544.",
    },
  ],
};

const DIAGRAM = (
  <div
    style={{
      display: "grid",
      gap: "0.625rem",
    }}
  >
    <svg
      viewBox="0 0 720 160"
      role="img"
      aria-label="INV.CONSISTENCY bridge from I1 construction to I2 observation"
      style={{ width: "100%", height: "auto", display: "block" }}
    >
      <rect
        x="20"
        y="26"
        width="260"
        height="108"
        rx="12"
        fill={withAlpha(THEOREM_COLOR["INV.ESTABLISH"], "1A")}
      />
      <rect
        x="440"
        y="26"
        width="260"
        height="108"
        rx="12"
        fill={withAlpha(THEOREM_COLOR["INV.PRESERVE"], "1A")}
      />
      <text
        x="40"
        y="54"
        fill={THEOREM_COLOR["INV.ESTABLISH"]}
        fontSize="12"
        fontWeight="700"
        letterSpacing="0.8"
      >
        I1 · INV.ESTABLISH
      </text>
      <text x="40" y="80" fill={GRAY[700]} fontSize="15" fontFamily={FONT.mono}>
        F(μF) →α μF
      </text>
      <text x="40" y="108" fill={GRAY[600]} fontSize="12">
        build only valid states
      </text>
      <text
        x="460"
        y="54"
        fill={THEOREM_COLOR["INV.PRESERVE"]}
        fontSize="12"
        fontWeight="700"
        letterSpacing="0.8"
      >
        I2 · INV.PRESERVE
      </text>
      <text x="460" y="80" fill={GRAY[700]} fontSize="15" fontFamily={FONT.mono}>
        νF →γ F(νF)
      </text>
      <text x="460" y="108" fill={GRAY[600]} fontSize="12">
        observe only valid transitions
      </text>
      <line x1="286" y1="80" x2="426" y2="80" stroke={GRAY[600]} strokeWidth="2" />
      <path d="M426 75 L434 80 L426 85 Z" fill={GRAY[600]} />
      <text x="314" y="70" fill={GRAY[700]} fontSize="12">
        canon : μF → νF
      </text>
      <text x="313" y="96" fill={ACCENT} fontSize="12" fontWeight="600">
        coherent by construction
      </text>
    </svg>
  </div>
);

export function InvConsistencyExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
